import { KycTier } from "@/types/kycReview";

// Absolute step indices (Tier 3 uses all of them; Tier 2 uses a subset).
export const STEP = {
  CLIENT: 0,
  ADDRESS: 1,
  PROFESSIONAL: 2,
  PEP: 3,
  FATCA: 4,
  RISK: 5,
  SETTLEMENT: 6,
  REVIEW: 7,
} as const;

export const STEP_TITLES: string[] = [
  "Client Information",
  "Address Details",
  "Professional Details",
  "PEP Declaration",
  "FATCA",
  "Risk Profiler",
  "Settlement Details",
  "Review & Submit",
];

export const STEP_SUBTITLES: string[] = [
  "Confirm your identity and contact details.",
  "Confirm where you live and work.",
  "Confirm your occupation and source of funds.",
  "Tell us about any government connections.",
  "Confirm your U.S. tax status.",
  "Confirm your investor risk profile.",
  "Confirm where settlements are credited.",
  "Review everything before you submit.",
];

export function isTier3Variant(
  tier: KycTier | null,
  formVariant: string
): boolean {
  return formVariant === "tier_3_refresh" || tier === 3;
}

export function isTier2Variant(
  tier: KycTier | null,
  formVariant: string
): boolean {
  return formVariant === "tier_2_refresh" || tier === 2;
}

// Tier 3 visits every step; Tier 2 skips PEP/FATCA/Settlement.
export function visibleSteps(isTier3: boolean): number[] {
  return isTier3
    ? [STEP.CLIENT, STEP.ADDRESS, STEP.PROFESSIONAL, STEP.PEP, STEP.FATCA, STEP.RISK, STEP.SETTLEMENT, STEP.REVIEW]
    : [STEP.CLIENT, STEP.ADDRESS, STEP.PROFESSIONAL, STEP.RISK, STEP.REVIEW];
}

export function stepLabel(index: number, isTier2: boolean): string {
  if (isTier2 && index === STEP.ADDRESS) return "Current Address";
  if (isTier2 && index === STEP.PROFESSIONAL) return "Work & IDs";
  return STEP_TITLES[index];
}

export function tierDisplay(isTier3: boolean, isTier2: boolean): string {
  if (isTier3) return "Tier 3 KYC refresh";
  if (isTier2) return "Tier 2 KYC refresh";
  return "KYC refresh";
}

// Copy — sourced from the design handoff (success/pending/timeline wording).
export const COPY = {
  pageTitle: "Update KYC Application",
  breadcrumb: "Account / Update information",
  infoBannerTitle: "You're updating verified information.",
  infoBannerNote:
    "Changes are checked by our team before they take effect \u2014 fields marked Verified are locked.",
  lockedHelper:
    "Verified and locked. To change this you'll need to re-verify your identity.",
  confirmLabel:
    "I confirm that the information I reviewed is correct and updated where necessary.",
  sidebarTitle: "Your Steps",
  whatHappensNextTitle: "What happens next",
  whatHappensNext:
    "Submit your update \u2192 our team reviews it \u2192 approved changes take effect. You can keep using your account while it's in review.",
  submitted: {
    title: "Your update is in review",
    body:
      "Thanks \u2014 we've received your account update. Our team will review your changes before they take effect. You can keep using your account in the meantime.",
    pill: "In review",
    timeline: ["Submitted", "Under review", "Approved"],
    primary: "Return to dashboard",
    tier2Secondary: "Upgrade to Tier 3 (full verification)",
  },
  pending: {
    title: "Your account is under review",
    body:
      "You already have an account update awaiting our team's review. You can't submit another update until the current one is reviewed. You can keep using your account in the meantime.",
    pill: "In review",
    primary: "Return to dashboard",
  },
  error: {
    retry: "Retry",
    generic:
      "We could not submit your account update. Please try again.",
    loadGeneric: "We could not load your account update details.",
    blockedSubmit: "Please complete the highlighted fields before submitting.",
  },
} as const;
